import { COLORS } from 'shared/styles/variables';
import styled from 'styled-components';

import AttributeItem, { AttributeGrid } from 'dashboard/components/AttributeItem';
import TableLayout, { TableTD, TableTH, TableTR } from 'dashboard/layouts/TableLayout';
import { ReportPhoneNumber } from 'dashboard/models/report/phone-number';
import Card from 'design-system/components/Card';
import * as Spacing from 'design-system/variables/spacing';
import ImageCircleAlert from 'images/dashboard/circle-alert.svg';
import ImageCirclePositive from 'images/dashboard/circle-positive.svg';

const RiskAssessmentSafeIcon = styled(ImageCirclePositive)`
  width: ${Spacing.BASE * 3}px;
  margin-right: ${Spacing.BASE * 2}px;
  margin-left: ${Spacing.BASE * 2}px;
  margin-top: ${Spacing.BASE}px;
  > * {
    stroke: ${COLORS.green100};
  }
`;

const RiskAssessmentUnsafeIcon = styled(ImageCircleAlert)`
  width: ${Spacing.BASE * 3}px;
  margin-right: ${Spacing.BASE * 2}px;
  margin-left: ${Spacing.BASE * 2}px;
  margin-top: ${Spacing.BASE}px;
  > * {
    stroke: ${COLORS.red100};
  }
`;

const RiskAssessmentCautionIcon = styled(ImageCircleAlert)`
  width: ${Spacing.BASE * 3}px;
  margin-right: ${Spacing.BASE * 2}px;
  margin-left: ${Spacing.BASE * 2}px;
  margin-top: ${Spacing.BASE}px;
  > * {
    stroke: ${COLORS.yellow100};
  }
`;

const RiskAssessmentTD = styled(TableTD)`
  padding: ${Spacing.BASE}px 0;
`;

const IconTH = styled(TableTH)`
  width: ${Spacing.BASE}px;
`;

const RiskAssessmentTH = styled(TableTH)`
  width: ${Spacing.BASE * 30}px;
  padding: 12px 0;
`;

const RiskAssessmentValueTH = styled(TableTH)`
  width: ${Spacing.BASE * 70}px;
  padding: 12px 0;
`;

const NonCarded = styled.div`
  > div {
    margin-bottom: ${Spacing.BASE * 2}px;
  }

  > h6 {
    padding-top: ${Spacing.BASE}px;
    margin-bottom: ${Spacing.BASE * 2}px;
  }
`;

interface PhoneNumberProps {
  report: ReportPhoneNumber;
  multiCardFormat: boolean;
}

const PhoneNumber = ({ report, multiCardFormat = false }: PhoneNumberProps) => {
  const {
    'risk-level': riskLevel,
    'risk-recommendation': riskRecommendation,
    'risk-score': riskScore,
    'risk-sim-swap': riskSimSwap,
  } = report.attributes;
  const showRiskAssessment = riskLevel || riskRecommendation || riskScore || riskSimSwap;

  if (multiCardFormat) {
    return (
      <>
        {showRiskAssessment && (
          <Card data-test={`report-details__risk-assessment--${report.id}`}>
            <Card.Body>
              <RiskAssessment report={report} />
            </Card.Body>
          </Card>
        )}
        <Card data-test={`report-details__phone-number-information--${report.id}`}>
          <Card.Body>
            <PhoneInformation report={report} />
          </Card.Body>
        </Card>
      </>
    );
  } else {
    return (
      <NonCarded>
        {showRiskAssessment && <RiskAssessment report={report} />}
        <PhoneInformation report={report} />
      </NonCarded>
    );
  }
};

const RiskAssessment = ({ report }: { report: ReportPhoneNumber }) => {
  const {
    'risk-level': riskLevel,
    'risk-recommendation': riskRecommendation,
    'risk-score': riskScore,
    'risk-sim-swap': riskSimSwap,
  } = report.attributes;

  let riskLevelIcon;
  switch (riskLevel) {
    case 'high':
    case 'medium-high':
      riskLevelIcon = <RiskAssessmentUnsafeIcon />;
      break;
    case 'medium':
      riskLevelIcon = <RiskAssessmentCautionIcon />;
      break;
    case 'medium-low':
    case 'low':
      riskLevelIcon = <RiskAssessmentSafeIcon />;
      break;
    default:
      riskLevelIcon = <RiskAssessmentUnsafeIcon />;
  }

  let riskRecommendationIcon;
  switch (riskRecommendation) {
    case 'block':
      riskRecommendationIcon = <RiskAssessmentUnsafeIcon />;
      break;
    case 'flag':
      riskRecommendationIcon = <RiskAssessmentCautionIcon />;
      break;
    default:
      riskRecommendationIcon = <RiskAssessmentSafeIcon />;
      break;
  }

  let riskScoreIcon;
  if (riskScore && riskScore > 60) {
    riskScoreIcon = <RiskAssessmentUnsafeIcon />;
  } else if (riskScore && riskScore > 40) {
    riskScoreIcon = <RiskAssessmentCautionIcon />;
  } else {
    riskScoreIcon = <RiskAssessmentSafeIcon />;
  }

  let riskSimSwapIcon;
  switch (riskSimSwap) {
    case 'high':
      riskSimSwapIcon = <RiskAssessmentUnsafeIcon />;
      break;
    case 'medium':
      riskSimSwapIcon = <RiskAssessmentCautionIcon />;
      break;
    case 'low':
    case 'very-low':
      riskSimSwapIcon = <RiskAssessmentSafeIcon />;
      break;
    default:
      // TODO: (malav) We have to figure out with product if this should be SafeIcon instead. For now, it is behind
      //  feature flag so it should be okay. Also, I’d rather have someone complain that something marked as unsafe is
      //  safe than complain that something marked as safe is actually unsafe.
      riskSimSwapIcon = <RiskAssessmentUnsafeIcon />;
  }

  return (
    <>
      <Card.Title>Risk assessment</Card.Title>
      <TableLayout
        dataTest='risk-assessment-table'
        isLoading={false}
        showHeader={true}
        isEmpty={false}
        nullState={null}
        headerTR={
          <>
            <IconTH data-test='risk-assessment-table__icon-header' />
            <RiskAssessmentTH data-test='risk-assessment-table__risk-property-header'>
              Risk Property
            </RiskAssessmentTH>
            <RiskAssessmentValueTH data-test='risk-assessment-table__value-header'>
              Value
            </RiskAssessmentValueTH>
          </>
        }
      >
        {riskSimSwap && (
          <TableTR
            data-test='risk-assessment-table__row--risk-sim-swap'
            id='risk-assessment-table__row--risk-sim-swap'
            key='risk-sim-swap'
          >
            <RiskAssessmentTD>{riskSimSwapIcon}</RiskAssessmentTD>
            <RiskAssessmentTD>Risk Sim Swap</RiskAssessmentTD>
            <RiskAssessmentTD>{translateRiskSimSwap(riskSimSwap)}</RiskAssessmentTD>
          </TableTR>
        )}
        {riskLevel && (
          <TableTR
            data-test='risk-assessment-table__row--risk-level'
            id='risk-assessment-table__row--risk-level'
            key='risk-level'
          >
            <RiskAssessmentTD>{riskLevelIcon}</RiskAssessmentTD>
            <RiskAssessmentTD>Risk Level (max: 5)</RiskAssessmentTD>
            <RiskAssessmentTD>{translateRiskLevel(riskLevel)}</RiskAssessmentTD>
          </TableTR>
        )}
        {riskRecommendation && (
          <TableTR
            data-test='risk-assessment-table__row--risk-recommendation'
            id='risk-assessment-table__row--risk-recommendation'
            key='risk-recommendation'
          >
            <RiskAssessmentTD>{riskRecommendationIcon}</RiskAssessmentTD>
            <RiskAssessmentTD>Risk Recommendation</RiskAssessmentTD>
            <RiskAssessmentTD>
              {riskRecommendation.charAt(0).toUpperCase() + riskRecommendation.slice(1)}
            </RiskAssessmentTD>
          </TableTR>
        )}
        {riskScore && (
          <TableTR
            data-test='risk-assessment-table__row--risk-score'
            id='risk-assessment-table__row--risk-score'
            key='risk-score'
          >
            <RiskAssessmentTD>{riskScoreIcon}</RiskAssessmentTD>
            <RiskAssessmentTD>Risk Score</RiskAssessmentTD>
            <RiskAssessmentTD>{riskScore} / 100</RiskAssessmentTD>
          </TableTR>
        )}
      </TableLayout>
    </>
  );
};

const PhoneInformation = ({ report }: { report: ReportPhoneNumber }) => {
  const { 'phone-type': phoneType, 'phone-carrier': phoneCarrier } = report.attributes;

  return (
    <>
      <Card.Title>Phone Number Information</Card.Title>

      <AttributeGrid>
        <AttributeItem
          title='Phone Type'
          value={translatePhoneType(phoneType)}
          placeholder='phoneType'
        />
        <AttributeItem title='Phone Carrier' value={phoneCarrier} placeholder='carrier' />
      </AttributeGrid>
    </>
  );
};

function translateRiskSimSwap(riskSimSwap?: string) {
  return riskSimSwap
    ? riskSimSwap
        .split('-')
        .map((str) => str.replace(/^./, str[0].toUpperCase()))
        .join(' ')
    : '';
}
function translatePhoneType(phoneType?: string) {
  if (!phoneType) {
    return '';
  }

  switch (phoneType) {
    case 'FIXED_LINE':
      // Fixed-line phones include traditional landline phones and VOIP phones that can be traced back to
      // a particular address and cannot be obtained by a user outside the phone number’s designated country.
      // Because these phone numbers are traceable, they are low-risk.
      return 'Fixed line';
    case 'MOBILE':
      // Mobile telephones are provided by companies such as Verizon, Cingular, or Sprint. Users of these
      // phones must sign contracts, making these telephone numbers traceable. These phone numbers are
      // generally low-risk. However, some prepaid mobile phones will be identified as Mobile. Internationally,
      // phones identified as mobile can also include TETRA mobile phones, cordless access systems, proprietary
      // fixed radio access, and fixed cellular systems.
      return 'Cell phone';
    case 'PREPAID':
      // These telephones can be purchased anonymously at department stores. They work like other mobile
      // phones, but their users are not contracted with a mobile phone company. Prepaid mobile phones
      // generally cost between $30 and $100, making them unattractive for most fraudsters, but TeleSign
      // recommends flagging transactions made with this type of telephone due to users’ anonymity. Also, not
      // all prepaid mobile phones will be identified as prepaid; some will be identified as Mobile.
      return 'Prepaid phone';
    case 'TOLL_FREE':
      // Toll-Free numbers are numbers starting with toll-free area codes, such as (800). Although often
      // used by legitimate users calling from companies, fraudsters can easily obtain toll-free numbers
      // that either forward to a telephone located in another country or toll-free numbers that terminate
      // at an Internet voicemail box. Toll-free numbers always forward to non-toll-free numbers. For this
      // reason, any legitimate user who provides a toll-free number and is asked to provide a non-toll-free
      // number will be able to do so. TeleSign therefore recommends blocking users of toll-free numbers.
      return 'Toll free number';
    case 'VOIP':
      // Voice Over Internet Protocol (VOIP) phone numbers are Internet-based telephone numbers.
      // Non-fixed VOIP telephone numbers can easily be obtained by users who are not located in the country
      // associated with the telephone number. They are untraceable and disposable; some can even be obtained
      // for free. This means that a fraudster in Romania could easily obtain a US-based telephone number
      // using a non-fixed VOIP service and receive a call to this number in his or her home in Romania.
      return 'Internet based telephone number';
    case 'PAGER':
      // This phone number rings to a pager. Because pagers cannot receive verification calls, it’s best to
      // block calls to these numbers. These calls are most likely requested by fraudsters who are inputting
      // invalid phone numbers.
      return 'Pager';
    case 'PAYPHONE':
      // Calls to a payphone cannot be traced back to any one person; therefore, we recommend blocking
      // verification calls to these numbers.
      return 'Payphone';
    case 'INVALID':
      // The telephone number entered is not a valid number.
      return 'Invalid number';
    case 'RESTRICTED_PREMIUM':
      // The telephone number entered is a restricted number. In the US, restricted numbers include numbers
      // that begin with 0 and numbers with the following area codes: 900, 911, 411. Internationally,
      // the following phone types are restricted: Audiotext, Carrier selection, National rate, Premium rate,
      // Shared cost, Satellite, Short Code
      return 'Restricted number';
    case 'PERSONAL':
      // A “Personal” phone number is a phone number which allows a Subscriber to receive calls at almost
      // any telephone number, including a mobile number. The “Personal” number forwards to another phone
      // number, which is determined by the subscriber. Because the “Personal” number can forward to either
      // a landline or mobile number, it’s impossible to determine what type of phone the subscriber is using.
      // However, because “Personal” phone numbers must be obtained from a phone company, there is little risk
      // of fraud associated with this phone type.
      return 'Personal / Forrwarded phone number';
    case 'VOICEMAIL':
      // Voicemail phone numbers will ring directly to a user’s voicemail. Although this phone number was
      // likely purchased through a phone company and may be traceable, it will be impossible to reach the user
      // live at this phone number. In addition, any user providing a voicemail number should also have a valid
      // landline or mobile phone available. Therefore, TeleSign recommends blocking this type of phone.
      return 'Voicemail number';
    case 'OTHER':
      // Phone numbers labeled “Other” are all various types of phones that originate in non-US countries.
      // The following types of phones may be included under the “Other” category: Global title address,
      // Inbound routing code, Videotext, VPN. Because clients should not be requesting calls to these
      // phone types, we recommend blocking verification calls to these numbers. Any phone number entered
      // that falls under the “Other” category was probably either entered incorrectly or was entered by a
      // fraudster testing various phone numbers.
      return 'Other / Non-US phone number';
    default:
      // Return original value. Probably need to add a new translation here.
      return phoneType;
  }
}

function translateRiskLevel(riskLevel: string) {
  switch (riskLevel) {
    case 'high':
      return 'Level 5: high risk';
    case 'medium-high':
      return 'Level 4: medium to high risk';
    case 'medium':
      return 'Level 3: medium risk';
    case 'medium-low':
      return 'Level 2: low to medium risk';
    case 'low':
      return 'Level 1: low risk';
    default:
      // The leveling has changed!
      return riskLevel;
  }
}

export default PhoneNumber;
